import type { ApiClient } from './client';

export interface PlanPeriod {
  months: number;
  stars: number;
  auto_renew: boolean;
  original_stars?: number;
  discount_pct?: number;
  checkout_modes?: string[];
  /** Цена картой в рублях: у пакета — ЮKassa или Tribute, у срока тарифа — ЮKassa (режим card) */
  rub?: number | null;
  checkout_kind?: 'new' | 'up' | 'ext' | 'renew' | null;
  unavailable_reason?: string | null;
}
export interface CommercePlan {
  id: string;
  revision: number;
  name: string;
  circle_per_month: number;
  voices_limit: number;
  periods: PlanPeriod[];
  dev_only: boolean;
  price_version: string;
  gift_checkout_modes?: string[];
}
export interface PlanCatalog {
  items: CommercePlan[];
  free: { voices_limit: number };
  current_plan_id: string | null;
  period_days: number;
  card_enabled: boolean;
  gifts_available?: boolean;
}
export interface CommerceSubscription {
  circle_per_month?: number | null;
  voices_limit?: number | null;
  scheduled_invoice?: {
    id: string;
    state: 'pending' | 'scheduled' | 'canceled' | 'completed';
    reason?: string | null;
    plan_id: string;
    months: number;
    due_at: string;
  } | null;
  schedule_delivery?: {
    schedule_id: string;
    state: string;
    due_at: string;
    invoice_id?: string | null;
    delivery_job_id?: string | null;
    delivery_state?: string | null;
    error_code?: string | null;
  } | null;
  plan_id: string;
  name?: string;
  state: 'active' | 'expired';
  via: string;
  auto_renew: boolean;
  current_period_start: string;
  current_period_end: string;
  paid_until: string;
  months: number;
  version: number;
  next: { plan_id: string; months: number } | null;
  actions?: { cancel: boolean; resume: boolean; downgrade: boolean; extend: boolean; upgrade: boolean };
}
export interface PackCatalog {
  available: boolean;
  items: Array<{
    id: string;
    name: string;
    circle: number;
    revision: number;
    dev_only: boolean;
    price_version?: string;
    periods: PlanPeriod[];
  }>;
}
export type CommerceCommand =
  | { kind: 'plan'; id: string; months: number; revision: number; priceVersion: string; via?: 'dev_free' | 'stars' | 'card' }
  | { kind: 'pack'; id: string; revision: number; priceVersion?: string; expectedStars?: number; via?: 'dev_free' | 'stars' | 'card' }
  | { kind: 'cancel' | 'resume' }
  | { kind: 'downgrade'; id: string; months: number; revision?: number; priceVersion?: string }
  | { kind: 'promo'; code: string }
  | {
      kind: 'gift';
      id: string;
      revision: number;
      recipient: string;
      wish: string;
      signed: boolean;
      priceVersion?: string;
      via?: 'dev_free' | 'stars' | 'card';
    }
  | { kind: 'gift_activate' | 'gift_share'; code: string };
export interface CommerceGift {
  code: string;
  status: string;
  plan: { id: string; name: string; circle_per_month: number; voices_limit: number };
  recipient_username: string;
  wish: string;
  signed: boolean;
  from: { name: string; photo_url: string | null } | null;
  can_share: boolean;
  link: string | null;
}

export interface CommerceResult {
  schedule_id?: string;
  due_at?: string;
  kind?: string;
  outcome?: string | { plan_id?: string; months?: number; paid_until?: string; circle_now?: number; next_grant_at?: string };
  provider?: 'stars' | 'tribute' | 'yookassa';
  open?: 'invoice' | 'telegram_link' | 'link';
  invoice_type?: 'subscription' | 'one_time';
  next_grant_at?: string | null;
  url?: string;
  invoice_id?: string;
  error_code?: string | null;
  can_cancel?: boolean;
  precheckout_pending?: boolean;
  delivery?: { state: string };
  dev_only?: boolean;
  amount?: number;
  circle_now?: number;
  entitlement_expired?: boolean;
  code?: string;
  gift_code?: string;
  plan?: CommerceGift['plan'];
  applied?: boolean;
  bound?: boolean;
  bonus_expires_at?: string | null;
  expires_at?: string | null;
  batch?: { amount: number; expires_at: string };
  state?: string;
  prepared_message_id?: string | null;
  subscription?: CommerceSubscription | null;
}

/** Оплата разрешена только явно объявленным сервером dev_free способом. */
export const commerce = (client: ApiClient) => ({
  payment: (id: string, signal?: AbortSignal) => client.request<CommerceResult>(`/app/payments/${encodeURIComponent(id)}`, { signal }),
  cancelPayment: (id: string, key: string) =>
    client.request<CommerceResult>(`/app/payments/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {}, idempotency: key }),
  gift: (code: string, signal?: AbortSignal) => client.request<CommerceGift>(`/app/gifts/${encodeURIComponent(code)}`, { signal }),
  plans: (signal?: AbortSignal) => client.request<PlanCatalog>('/app/plans', { signal }),
  subscription: (signal?: AbortSignal) => client.request<CommerceSubscription | null>('/app/subscription', { signal }),
  packs: (signal?: AbortSignal) => client.request<PackCatalog>('/app/packs', { signal }),
  command: (command: CommerceCommand, key: string, signal?: AbortSignal) => {
    const path =
      command.kind === 'promo'
        ? '/app/promo/apply'
        : command.kind === 'gift'
          ? '/app/gifts'
          : command.kind === 'gift_activate'
            ? `/app/gifts/${encodeURIComponent(command.code)}/activate`
            : command.kind === 'gift_share'
              ? `/app/gifts/${encodeURIComponent(command.code)}/share`
              : command.kind === 'plan'
                ? `/app/plans/${encodeURIComponent(command.id)}/checkout`
                : command.kind === 'pack'
                  ? `/app/packs/${encodeURIComponent(command.id)}/checkout`
                  : `/app/subscription/${command.kind}`;
    const body =
      command.kind === 'promo'
        ? { code: command.code }
        : command.kind === 'gift'
          ? {
              plan_id: command.id,
              expected_revision: command.revision,
              price_version: command.priceVersion,
              recipient_username: command.recipient,
              wish: command.wish,
              signed: command.signed,
              via: command.via ?? 'dev_free',
            }
          : command.kind === 'plan'
            ? {
                via: command.via ?? 'dev_free',
                months: command.months,
                expected_revision: command.revision,
                price_version: command.priceVersion,
              }
            : command.kind === 'pack'
              ? {
                  via: command.via ?? 'dev_free',
                  expected_revision: command.revision,
                  price_version: command.priceVersion,
                  expected_stars: command.expectedStars,
                }
              : command.kind === 'downgrade'
                ? { plan_id: command.id, months: command.months, expected_revision: command.revision, price_version: command.priceVersion }
                : undefined;
    return client.request<CommerceResult>(path, { method: 'POST', body, idempotency: key, signal });
  },
});
